<script setup lang="ts">
import { AppFlicker, AppSvgIcon } from '@libs/ui';

import type { PeekPriceSmallBookableTransportCardData } from '../models';

interface PeekPriceSmallBookableTransportCardProps {
  data: PeekPriceSmallBookableTransportCardData;
}

const props = defineProps<PeekPriceSmallBookableTransportCardProps>();
</script>

<template>
  <div class="peek-price-small-bookable-transport-card">
    <img
      v-if="props.data.carrierLogoUrl"
      :src="props.data.carrierLogoUrl"
      :alt="props.data.carrierLogoAltText"
      class="bookable-transport-carrier-logo"
    />
    <div v-else class="icon-wrapper">
      <AppSvgIcon :name="props.data.altLogoIcon" size="18" />
    </div>

    <div class="bookable-transport-card-content">
      <div class="time-section">
        <div v-if="props.data.date" class="date-text">
          {{ props.data.date }}
        </div>
        <div v-else class="date-text-flicker">
          <AppFlicker height="100%" width="spacing-40" />
        </div>
        <div v-if="props.data.stops" class="stops-text">
          {{ props.data.stops }}
        </div>
        <div v-else class="stops-text-flicker">
          <AppFlicker height="100%" width="spacing-40" />
        </div>
      </div>
      <div v-if="props.data.time" class="time-text">{{ props.data.time }}</div>
      <div v-else class="time-text-flicker">
        <AppFlicker height="100%" width="spacing-80" />
      </div>
      <div v-if="props.data.ports" class="ports-text">
        {{ props.data.ports }}
      </div>
      <div v-else class="ports-text-flicker">
        <AppFlicker height="100%" width="spacing-80" />
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.peek-price-small-bookable-transport-card {
  --size: 60px;
  display: flex;
  gap: var(--spacing-12);

  .bookable-transport-carrier-logo {
    width: var(--size);
    height: var(--size);
    object-fit: contain;
    border-radius: var(--border-radius-8);
  }

  .icon-wrapper {
    width: var(--size);
    height: var(--size);

    display: flex;
    align-items: center;
    justify-content: center;
  }

  .bookable-transport-card-content {
    display: flex;
    flex-direction: column;
    gap: var(--spacing-4);

    .time-section {
      display: flex;

      .date-text {
        @include font-label-s-light;
        color: var(--color-neutral-70);
        padding-right: var(--spacing-8);
        border-right: 1px solid var(--color-neutral-20);
        margin-right: var(--spacing-8);
      }

      .date-text-flicker {
        padding-right: var(--spacing-8);
        border-right: 1px solid var(--color-neutral-20);
        margin-right: var(--spacing-8);
      }

      .stops-text {
        @include font-label-s-light;
        color: var(--color-neutral-70);
      }

      .stops-text-flicker {
        height: 15px;
      }
    }

    .time-text {
      @include font-label-m-bold;
      color: var(--color-neutral-70);
    }

    .time-text-flicker {
      height: 18px;
    }

    .ports-text {
      @include font-label-s-light;
      color: var(--color-neutral-70);
    }

    .ports-text-flicker {
      height: 15px;
    }
  }
}
</style>
